// The chat window's size, kept in the browser so a reload opens it as big as it was left.

export const AI_CHAT_DEFAULT_WIDTH = 400;
export const AI_CHAT_DEFAULT_HEIGHT = 560;
export const AI_CHAT_MIN_WIDTH = 280;
export const AI_CHAT_MIN_HEIGHT = 380;

/**
 * The window is anchored at the chat icon; this much of the screen stays free around it: its own frame and head,
 * the icon below it and a little room at the screen's edges, so the whole window, head and close button included,
 * is on the screen.
 */
const SCREEN_MARGIN_X = 48;
const SCREEN_MARGIN_Y = 120;
/** On a screen smaller than the minimum the window shrinks with it, down to this; the conversation scrolls inside. */
const SMALLEST = { width: 200, height: 160 };

export const CHAT_SIZE_KEY = "xm-chat-window-size";

export interface ChatWindowSize {
  width: number;
  height: number;
}

export const DEFAULT_CHAT_SIZE: ChatWindowSize = { width: AI_CHAT_DEFAULT_WIDTH, height: AI_CHAT_DEFAULT_HEIGHT };

type SizeStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserStore(): SizeStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** At least the minimum, at most what the screen leaves for it: on a small screen the screen wins over the minimum. */
export function fitChatSize(size: ChatWindowSize, screen: ChatWindowSize): ChatWindowSize {
  const maxWidth = Math.max(SMALLEST.width, screen.width - SCREEN_MARGIN_X);
  const maxHeight = Math.max(SMALLEST.height, screen.height - SCREEN_MARGIN_Y);
  return {
    width: Math.round(Math.min(maxWidth, Math.max(AI_CHAT_MIN_WIDTH, size.width))),
    height: Math.round(Math.min(maxHeight, Math.max(AI_CHAT_MIN_HEIGHT, size.height))),
  };
}

/** The size left last time, or null when none was kept (or the browser keeps nothing). */
export function readChatSize(store: SizeStore | null = browserStore()): ChatWindowSize | null {
  try {
    const kept: unknown = JSON.parse(store?.getItem(CHAT_SIZE_KEY) ?? "null");
    if (typeof kept !== "object" || kept === null) {
      return null;
    }
    const { width, height } = kept as Record<string, unknown>;
    return typeof width === "number" && typeof height === "number" && Number.isFinite(width) && Number.isFinite(height)
      ? { width, height }
      : null;
  } catch {
    return null;
  }
}

export function keepChatSize(size: ChatWindowSize, store: SizeStore | null = browserStore()): void {
  try {
    store?.setItem(CHAT_SIZE_KEY, JSON.stringify({ width: Math.round(size.width), height: Math.round(size.height) }));
  } catch {
    // a browser that keeps nothing simply opens the default size next time
  }
}

export function forgetChatSize(store: SizeStore | null = browserStore()): void {
  try {
    store?.removeItem(CHAT_SIZE_KEY);
  } catch {
    // nothing kept, nothing to forget
  }
}

/** Above this share of the screen's width, or of its area, the window hides the page a link opens under it. */
const COVERS_WIDTH_SHARE = 0.5;
const COVERS_AREA_SHARE = 0.4;

/**
 * True when the window covers the page enough that a page opened from it would not be seen: enlarged, wider
 * than half the screen, or over 40 % of its area. Such a window steps aside to its default size (AIChatPopover).
 */
export function coversPage(size: ChatWindowSize, screen: ChatWindowSize, enlarged: boolean): boolean {
  if (enlarged) {
    return true;
  }
  if (screen.width <= 0 || screen.height <= 0) {
    return false;
  }
  return size.width > screen.width * COVERS_WIDTH_SHARE || size.width * size.height > screen.width * screen.height * COVERS_AREA_SHARE;
}

/**
 * True while a modal, an image preview or a page's drawer stands over the page: then Escape is theirs, not the chat's.
 * A drawer closes on Escape from the window, after this has heard it, and is marked open (ant-drawer-open) until then.
 */
export function overlayOpen(root: ParentNode = document): boolean {
  return [...root.querySelectorAll<HTMLElement>(".ant-modal-wrap, .ant-image-preview, .ant-drawer-open")]
    .some((layer) => layer.style.display !== "none" && !layer.closest("[style*='display: none']"));
}
